import { createFileRoute } from "@tanstack/react-router";
import { products } from "@/data/products";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/cornhole-boards")({
  head: () => ({
    meta: [
      { title: "Custom Cornhole Boards | ACA Spec Sets | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Hand-built cornhole boards from Paragould, Arkansas. 24 x 48 inch ACA specification sets from $250, plus LED-lit boards for night play at $400.",
      },
      { property: "og:title", content: "Hand-built cornhole boards, ACA specification" },
      {
        property: "og:description",
        content:
          "Trimmed in red, green, orange or yellow with three coats of polyurethane. LED-lit boards coming soon.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/cornhole-boards") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/cornhole-boards") }],
  }),
  component: CornholeBoards,
});

const specs: Array<[string, string]> = [
  ["Playing surface", "3/4 inch hardwood plywood, sanded flat for a true slide"],
  ["Frame", "Solid 2x4 framing lumber, glued and screwed"],
  ["Size", "24 by 48 inches — ACA regulation"],
  ["Finish", "Three coats of polyurethane over the top"],
  ["LED boards", "Lights embedded in the top surface for night-time play"],
  ["Built by hand", "Every set made one at a time in Paragould, Arkansas"],
];

function CornholeBoards() {
  const all = products.filter((p) => p.subcategory === "Cornhole Boards");
  const plain = all.filter((p) => !p.slug.endsWith("-led") && p.slug.startsWith("cornhole-boards-"));
  const led = all.filter((p) => p.slug.endsWith("-led"));
  const custom = all.filter((p) => p.slug === "custom-cornhole-boards");

  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Cornhole Boards" }]} />
      <PageHeader
        eyebrow="Cornhole boards"
        title="Built to play on, not to look at."
        lede="Hand-crafted from 2x4s and 3/4 inch plywood, finished for a true slide, and sized to ACA regulation. Choose your trim colour — or a set with LED lights for games after dark."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Request a Set
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="cornhole-specs-heading">
        <div className="shell">
          <SectionHeading
            id="cornhole-specs-heading"
            eyebrow="The build"
            title="What goes into every set."
            lede="No flat-pack hardware, no thin tops that flex under a bag. These are built the way a set that lives outside all summer has to be built."
          />
          <dl className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
            {specs.map(([term, detail]) => (
              <div key={term} className="bg-background p-7">
                <dt className="eyebrow text-[0.65rem]">{term}</dt>
                <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <Section labelledBy="cornhole-standard-heading">
        <div className="shell">
          <SectionHeading
            id="cornhole-standard-heading"
            eyebrow="$250 per set"
            title="Cornhole boards without lights."
            lede="Hand-crafted from 2x4s and 3/4 inch thick plywood. Trimmed in your colour with three coats of polyurethane, 24 by 48 inches, hardwood plywood tops, meeting ACA specifications."
          />
          <ProductGrid products={plain} showRequestSimilar={false} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="cornhole-led-heading">
        <div className="shell">
          <SectionHeading
            id="cornhole-led-heading"
            eyebrow="$400 per set — coming soon"
            title="Cornhole boards with LED lights."
            lede="LED lights are embedded in the top surface so play carries on after sundown. The bottom, sides and legs take one coat of polyurethane; the top is painted and takes three. These boards meet or exceed the size, strength and rigidity regulations of the ACA and ACO."
          />
          <ProductGrid products={led} showRequestSimilar={false} columns={3} />
          <p className="mt-8 text-sm text-muted-foreground">
            LED sets are in production now. Ask Paul to put your colour on the list and he will let you know
            the moment the first run is finished.
          </p>
        </div>
      </Section>

      {custom.length > 0 && (
        <Section labelledBy="cornhole-custom-heading">
          <div className="shell">
            <SectionHeading
              id="cornhole-custom-heading"
              eyebrow="Made your way"
              title="Team colours, family names, business logos."
              lede="Tops can be painted or CNC-carved with your artwork before the finish goes on. Tell Paul what the set is for and he will tell you what is possible."
            />
            <ProductGrid products={custom} showRequestSimilar={false} columns={2} />
          </div>
        </Section>
      )}

      <FinalCTA heading="Tell Paul your colours and he will build the set." />
    </>
  );
}
